/**
 * CurbConfirmation Component
 * Review/confirmation page for curb types (looped section)
 * Displays all curb data from database in organized sections
 */

import React, { useState } from "react";
import FilePreviewModalSimple from "../FileUpload/FilePreviewModalSimple";

const CurbConfirmation = ({
  currentData,
  onEdit,
  onConfirm,
  onBack,
  canGoBack = false,
  isCompleted = false,
}) => {
  const [previewImage, setPreviewImage] = useState(null);

  const renderYesNoAnswer = (label, value, editKey) => {
    if (value === undefined || value === null) return null;

    return (
      <div style={{ marginBottom: "15px" }}>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
            <span
              style={{
                fontWeight: "bold",
                fontSize: "15px",
                color: "var(--text-primary)",
              }}
            >
              {label}
            </span>
            <div style={{ display: "flex", gap: "10px" }}>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === true
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === true ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                Yes
              </button>
              <button
                disabled
                style={{
                  padding: "8px 25px",
                  fontSize: "14px",
                  border: "1px solid var(--border-primary)",
                  backgroundColor:
                    value === false
                      ? "var(--surface-hover)"
                      : "var(--surface-secondary)",
                  cursor: "not-allowed",
                  fontWeight: value === false ? "bold" : "normal",
                  color: "var(--text-primary)",
                }}
              >
                No
              </button>
            </div>
          </div>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>
      </div>
    );
  };

  const renderDataSection = (title, data, editKey) => {
    // Filter out null/undefined/empty values from the data object
    const filteredData = {};
    Object.entries(data).forEach(([key, value]) => {
      // Skip N/A flag fields
      if (key.includes("_na")) return;

      // Skip fields where corresponding _na field is true
      const naFieldName = `${key}_na`;
      if (currentData[naFieldName] === true) return;

      // Skip fields with 'N/A' value
      if (value === "N/A") return;

      // Skip null, undefined, or empty string values
      if (value === null || value === undefined || value === "") return;

      // Skip empty arrays
      if (Array.isArray(value) && value.length === 0) return;

      // Include the value
      filteredData[key] = value;
    });

    if (Object.keys(filteredData).length === 0) return null;

    return (
      <div
        style={{
          marginBottom: "25px",
          padding: "15px",
          backgroundColor: "var(--surface-tertiary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          borderRadius: "4px",
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: "15px",
          }}
        >
          <h4
            style={{
              margin: 0,
              fontSize: "16px",
              color: "var(--text-primary)",
            }}
          >
            {title}
          </h4>
          <button
            onClick={() => onEdit(editKey)}
            style={{
              padding: "6px 20px",
              fontSize: "14px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-secondary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            Edit
          </button>
        </div>

        <div style={{ display: "grid", gap: "10px" }}>
          {Object.entries(filteredData).map(([key, value]) => {
            // Format the key name
            const formattedKey = key
              .replace(/_/g, " ")
              .replace(/\b\w/g, (char) => char.toUpperCase());

            // Handle different value types
            let displayValue = value;
            let isImageField = false;

            if (Array.isArray(value)) {
              isImageField = key.includes("image");
              if (!isImageField) {
                displayValue = `${value.length} item(s)`;
              }
            } else if (typeof value === "boolean") {
              displayValue = value ? "Yes" : "No";
            }

            // Render image thumbnails
            if (isImageField && Array.isArray(value) && value.length > 0) {
              return (
                <div key={key} style={{ marginBottom: "15px" }}>
                  <span
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginBottom: "8px",
                      color: "var(--text-primary)",
                    }}
                  >
                    {formattedKey}:
                  </span>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns:
                        "repeat(auto-fill, minmax(100px, 1fr))",
                      gap: "10px",
                      marginTop: "5px",
                    }}
                  >
                    {value.map((image, idx) => (
                      <div
                        key={image.id || idx}
                        style={{
                          border: "1px solid var(--border-secondary)",
                          padding: "5px",
                          backgroundColor: "var(--surface-secondary)",
                        }}
                      >
                        <img
                          src={image.url}
                          alt={
                            image.label || image.filename || `Image ${idx + 1}`
                          }
                          style={{
                            width: "100%",
                            height: "80px",
                            objectFit: "cover",
                            display: "block",
                            cursor: "pointer",
                          }}
                          onClick={() => setPreviewImage(image)}
                          onError={(e) => {
                            e.target.style.display = "none";
                            e.target.nextSibling.style.display = "flex";
                          }}
                        />
                        <div
                          style={{
                            display: "none",
                            height: "80px",
                            alignItems: "center",
                            justifyContent: "center",
                            backgroundColor: "var(--surface-tertiary)",
                            color: "var(--text-secondary)",
                            fontSize: "12px",
                          }}
                        >
                          Image
                        </div>
                        <p
                          style={{
                            margin: "5px 0 0 0",
                            fontSize: "11px",
                            color: "var(--text-secondary)",
                            whiteSpace: "nowrap",
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                          }}
                          title={image.label || image.filename}
                        >
                          {image.label || image.filename || `Image ${idx + 1}`}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              );
            }

            return (
              <div key={key} style={{ display: "flex", gap: "10px" }}>
                <span style={{ fontWeight: "500", minWidth: "250px" }}>
                  {formattedKey}:
                </span>
                <span>{displayValue}</span>
              </div>
            );
          })}
        </div>
      </div>
    );
  };

  return (
    <div className="curb-confirmation" style={{ padding: "20px" }}>
      <div
        style={{
          backgroundColor: "var(--surface-secondary)",
          color: "var(--text-primary)",
          border: "1px solid var(--border-secondary)",
          padding: "20px",
          borderRadius: "4px",
        }}
      >
        {/* Curb Type Name */}
        {currentData.curb_type_name && (
          <div
            style={{
              marginBottom: "25px",
              paddingBottom: "15px",
              borderBottom: "2px solid var(--border-secondary)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h3
              style={{
                margin: 0,
                fontSize: "18px",
                color: "var(--text-primary)",
              }}
            >
              {currentData.curb_type_name}
            </h3>
            <button
              onClick={() => onEdit("curb_type_name")}
              style={{
                padding: "6px 20px",
                fontSize: "14px",
                border: "1px solid var(--border-primary)",
                backgroundColor: "var(--surface-secondary)",
                color: "var(--text-primary)",
                cursor: "pointer",
              }}
            >
              Edit
            </button>
          </div>
        )}

        {/* Yes/No Questions */}
        <div style={{ marginBottom: "25px" }}>
          {renderYesNoAnswer(
            "Does this curb type have a gutter?",
            currentData.has_gutter,
            "has_gutter",
          )}
          {renderYesNoAnswer(
            "Is there a catch/spill distinction?",
            currentData.has_catch_spill_distinction,
            "has_catch_spill_distinction",
          )}
          {renderYesNoAnswer(
            "Do contour lines extend to the curb?",
            currentData.contours_to_curb,
            "contours_to_curb",
          )}
          {renderYesNoAnswer(
            "Does the linework match the detail width?",
            currentData.linework_matches_detail,
            "linework_matches_detail",
          )}
        </div>

        {/* Curb Detail */}
        {renderDataSection(
          "Curb Detail",
          {
            curb_detail_images: currentData.curb_detail_images,
          },
          "curb_detail_form",
        )}

        {/* Gutter Offsets */}
        {(currentData.has_gutter ||
          currentData.vertical_offset_to_top_back ||
          currentData.horizontal_offset_to_top_back ||
          currentData.vertical_offset_to_flowline) &&
          renderDataSection(
            "Gutter Offsets",
            {
              vertical_offset_to_top_back:
                currentData.vertical_offset_to_top_back,
              horizontal_offset_to_top_back:
                currentData.horizontal_offset_to_top_back,
              vertical_offset_to_flowline:
                currentData.vertical_offset_to_flowline,
            },
            "gutter_offsets_form",
          )}

        {/* Catch/Spill Indication */}
        {(currentData.has_catch_spill_distinction ||
          currentData.catch_spill_indication_methods ||
          currentData.catch_spill_indication_images) &&
          renderDataSection(
            "Catch/Spill Indication",
            {
              indication_methods: currentData.catch_spill_indication_methods,
              indication_images: currentData.catch_spill_indication_images,
            },
            "catch_spill_indication_form",
          )}

        {/* Catch/Spill Offset Details */}
        {(currentData.has_catch_spill_detail ||
          currentData.catch_curb_vertical_offset_top_back ||
          currentData.catch_curb_horizontal_offset_top_back ||
          currentData.catch_curb_vertical_offset_flowline ||
          currentData.spill_curb_vertical_offset_top_back ||
          currentData.spill_curb_horizontal_offset_top_back ||
          currentData.spill_curb_vertical_offset_flowline ||
          currentData.catch_spill_offset_images) &&
          renderDataSection(
            "Catch/Spill Offset Details",
            {
              catch_curb_vertical_offset_top_back:
                currentData.catch_curb_vertical_offset_top_back,
              catch_curb_horizontal_offset_top_back:
                currentData.catch_curb_horizontal_offset_top_back,
              catch_curb_vertical_offset_flowline:
                currentData.catch_curb_vertical_offset_flowline,
              spill_curb_vertical_offset_top_back:
                currentData.spill_curb_vertical_offset_top_back,
              spill_curb_horizontal_offset_top_back:
                currentData.spill_curb_horizontal_offset_top_back,
              spill_curb_vertical_offset_flowline:
                currentData.spill_curb_vertical_offset_flowline,
              offset_images: currentData.catch_spill_offset_images,
            },
            "catch_spill_offsets_form",
          )}

        {/* Assumed Catch/Spill Offsets */}
        {(currentData.manager_assume_catch_spill_difference ||
          currentData.assumed_spill_vertical_offset_top_back ||
          currentData.assumed_spill_horizontal_offset_top_back ||
          currentData.assumed_spill_vertical_offset_flowline) &&
          renderDataSection(
            "Assumed Catch/Spill Offsets",
            {
              assumed_spill_vertical_offset_top_back:
                currentData.assumed_spill_vertical_offset_top_back,
              assumed_spill_horizontal_offset_top_back:
                currentData.assumed_spill_horizontal_offset_top_back,
              assumed_spill_vertical_offset_flowline:
                currentData.assumed_spill_vertical_offset_flowline,
            },
            "assumed_catch_spill_offsets_form",
          )}

        {/* No Gutter Details */}
        {(currentData.has_gutter === false ||
          currentData.no_gutter_detail_images ||
          currentData.no_gutter_vertical_offset_top_back ||
          currentData.no_gutter_horizontal_offset_top_back) &&
          renderDataSection(
            "Curb Details (No Gutter)",
            {
              detail_images: currentData.no_gutter_detail_images,
              vertical_offset_top_back:
                currentData.no_gutter_vertical_offset_top_back,
              horizontal_offset_top_back:
                currentData.no_gutter_horizontal_offset_top_back,
            },
            "no_gutter_detail",
          )}

        {/* Contour Details */}
        {(currentData.contours_to_curb ||
          currentData.contours_break_at ||
          currentData.contours_images ||
          currentData.manager_model_full_gutter !== undefined) &&
          renderDataSection(
            "Contour Line Details",
            {
              contours_break_at: currentData.contours_break_at,
              contours_images: currentData.contours_images,
              manager_model_full_gutter: currentData.manager_model_full_gutter
                ? "Yes"
                : currentData.manager_model_full_gutter === false
                  ? "No"
                  : null,
            },
            "contours_form",
          )}

        {/* Curb Location Details */}
        {(currentData.curb_location_indication_methods ||
          currentData.curb_location_images) &&
          renderDataSection(
            "Curb Location Details",
            {
              location_indication_methods:
                currentData.curb_location_indication_methods,
              location_images: currentData.curb_location_images,
            },
            "curb_location_form",
          )}

        {/* Width Decision */}
        {(currentData.manager_width_decision ||
          currentData.linework_matches_detail === false) &&
          renderDataSection(
            "Width Decision",
            {
              manager_width_decision: currentData.manager_width_decision,
            },
            "manager_width_choice",
          )}
      </div>

      {/* Action Buttons */}
      <div
        style={{
          marginTop: "25px",
          display: "flex",
          gap: "15px",
          alignItems: "center",
        }}
      >
        {canGoBack && (
          <button
            onClick={onBack}
            style={{
              padding: "12px 40px",
              fontSize: "16px",
              border: "1px solid var(--border-primary)",
              backgroundColor: "var(--surface-tertiary)",
              color: "var(--text-primary)",
              cursor: "pointer",
            }}
          >
            ← Back
          </button>
        )}

        {/* Only show Confirm & Continue button if not yet completed */}
        {!isCompleted && (
          <button
            onClick={onConfirm}
            style={{
              padding: "var(--space-3) var(--space-8)",
              fontSize: "var(--text-base)",
              fontWeight: "var(--font-semibold)",
              border: "1px solid var(--cta-orange)",
              backgroundColor: "var(--cta-orange)",
              color: "white",
              cursor: "pointer",
              borderRadius: "var(--radius-md)",
              transition: "all var(--transition-fast)",
            }}
            onMouseEnter={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange-hover)";
            }}
            onMouseLeave={(e) => {
              e.target.style.backgroundColor = "var(--cta-orange)";
            }}
          >
            Confirm & Continue
          </button>
        )}
      </div>

      {/* Image Preview Modal */}
      <FilePreviewModalSimple
        file={previewImage}
        isOpen={!!previewImage}
        onClose={() => setPreviewImage(null)}
      />
    </div>
  );
};

export default CurbConfirmation;
